import React, { useState, useEffect, useCallback } from 'react';
import {
    collection,
    query,
    orderBy,
    limit,
    getDocs,
    getCountFromServer,
    where,
    Timestamp,
    type QueryDocumentSnapshot,
    type DocumentData,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

/** Normalize Firestore `to` (string | string[] | { email } | legacy shapes) for display. */
function recipientToText(to: unknown): string {
    if (to == null) return '';
    if (typeof to === 'string') return to;
    if (Array.isArray(to)) {
        return to
            .map((entry) =>
                typeof entry === 'string' ? entry : (entry as { email?: string })?.email || ''
            )
            .filter(Boolean)
            .join(', ');
    }
    if (typeof to === 'object' && to !== null && 'email' in (to as object)) {
        return String((to as { email?: string }).email || '');
    }
    return '';
}

// All known system email type values (kept in sync with functions/emailService.js TRANSACTIONAL_TAG_TYPES)
const SYSTEM_TYPE_LABELS: Record<string, string> = {
    verification: 'Email Verification',
    emailverification: 'Email Verification',
    password_reset: 'Password Reset',
    passwordreset: 'Password Reset',
    plan_activation: 'Plan Activation',
    invoice: 'Invoice',
    receipt: 'Receipt',
    billing_notice: 'Billing Notice',
    welcome_email: 'Welcome',
    admin_notification_registration: 'Registration Alert',
    admin_message: 'Admin Message',
    admin_password_reset: 'Admin Password Reset',
    sender_verification: 'Sender Verification',
    platform_broadcast: 'Platform Broadcast',
    system: 'System',
};

interface SystemEmail {
    id: string;
    to: unknown;
    from?: string;
    subject: string;
    type?: string;
    status?: string;
    timestamp?: Timestamp;
    sentAt?: Timestamp;
    createdAt?: Timestamp | Date;
    messageId?: string;
    error?: string;
    userId?: string;
    provider?: string;
    /** True when the row came from email_logs (supplement path) */
    _source?: 'system_emails' | 'email_logs';
}

interface EmailStats {
    total: number;
    success: number;
    failed: number;
    successRate: number;
}

const ROWS_PER_PAGE = 25;
// Fetch the 100 most-recent docs per collection.
// system_emails is ordered server-side so we always get the newest 100.
// email_logs is supplementary — 100 is ample to catch admin-triggered sends
// not already in system_emails. Both limits keep per-page-load read costs low.
const FETCH_BATCH = 100;

const AdminSystemEmails: React.FC = () => {
    const { currentUser } = useAuth();
    const { showError } = useNotification();

    const [emails, setEmails] = useState<SystemEmail[]>([]);
    const [displayed, setDisplayed] = useState<SystemEmail[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);

    const [filterType, setFilterType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const [stats, setStats] = useState<EmailStats>({ total: 0, success: 0, failed: 0, successRate: 0 });

    // ─── helpers ────────────────────────────────────────────────────────────────

    const toMillis = (value: any): number => {
        if (!value) return 0;
        if (typeof value?.seconds === 'number') return value.seconds * 1000;
        const parsed = new Date(value).getTime();
        return Number.isNaN(parsed) ? 0 : parsed;
    };

    const getTimestamp = (e: SystemEmail) => e.timestamp || e.sentAt || e.createdAt;

    const formatDate = (e: SystemEmail) => {
        const ts = getTimestamp(e);
        if (!ts) return '—';
        if ((ts as Timestamp)?.seconds) return new Date((ts as Timestamp).seconds * 1000).toLocaleString();
        try {
            const d = new Date(ts as any);
            return isNaN(d.getTime()) ? '—' : d.toLocaleString();
        } catch { return '—'; }
    };

    const typeLabel = (type: unknown) => {
        const key = String(type ?? '').toLowerCase();
        return SYSTEM_TYPE_LABELS[key] ?? (key || 'system');
    };

    const getStatusColor = (status: unknown) => {
        const s = String(status ?? '').toLowerCase();
        if (s === 'sent') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
        if (s === 'failed') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    };

    const getTypeColor = (type: unknown) => {
        const key = String(type ?? '').toLowerCase();
        if (key.includes('verification')) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
        if (key.includes('password') || key.includes('reset')) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
        if (key.includes('plan') || key.includes('invoice') || key.includes('billing') || key.includes('receipt')) return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
        if (key.includes('admin')) return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
        if (key.includes('sender')) return 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400';
        if (key.includes('welcome') || key.includes('broadcast')) return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    };

    // ─── data fetching ───────────────────────────────────────────────────────────

    const loadStats = useCallback(async () => {
        if (!currentUser) return;
        try {
            // Try system_emails first (dedicated collection, no index needed for count)
            let total = 0, failed = 0;
            try {
                const sysRef = collection(db, 'system_emails');
                const [totalSnap, failedSnap] = await Promise.all([
                    getCountFromServer(query(sysRef)),
                    getCountFromServer(query(sysRef, where('status', '==', 'failed'))),
                ]);
                total = totalSnap.data().count;
                failed = failedSnap.data().count;
            } catch {
                // Stats will be derived from loaded data instead (see useEffect below)
            }
            const success = total - failed;
            setStats({ total, success, failed, successRate: total > 0 ? (success / total) * 100 : 100 });
        } catch (err) {
            console.error('Error loading system email stats:', err);
        }
    }, [currentUser]);

    const loadEmails = useCallback(async () => {
        if (!currentUser) return;
        setIsLoading(true);
        try {
            const sysRef = collection(db, 'system_emails');

            // Fetch the 100 most-recent system emails, ordered newest-first.
            // limit(100) keeps per-load read cost at ≤100 reads regardless of
            // how many system emails have ever been sent.
            let sysSnap1: any = null;
            try {
                sysSnap1 = await getDocs(query(sysRef, orderBy('timestamp', 'desc'), limit(FETCH_BATCH)));
            } catch (e1: any) {
                console.warn('[SystemEmails] Ordered query failed, trying unordered:', e1?.message);
                try {
                    sysSnap1 = await getDocs(query(sysRef, limit(FETCH_BATCH)));
                } catch (e2: any) {
                    console.error('[SystemEmails] system_emails query failed:', e2?.message);
                }
            }

            const sysDocs: QueryDocumentSnapshot<DocumentData>[] = sysSnap1 ? [...sysSnap1.docs] : [];

            const fromSysEmails: SystemEmail[] = sysDocs.map(d => ({
                id: d.id,
                _source: 'system_emails' as const,
                ...d.data(),
            } as SystemEmail));

            // email_logs supplement — catches admin-triggered writes not in system_emails
            let fromEmailLogs: SystemEmail[] = [];
            try {
                const logsSnap = await getDocs(
                    query(collection(db, 'email_logs'), where('isSystemEmail', '==', true), limit(FETCH_BATCH))
                );
                fromEmailLogs = logsSnap.docs.map(d => ({
                    id: `log_${d.id}`,
                    _source: 'email_logs' as const,
                    ...d.data(),
                    timestamp: d.data().sentAt ?? d.data().timestamp ?? d.data().createdAt,
                } as SystemEmail));
            } catch (le: any) {
                console.warn('[SystemEmails] email_logs query failed:', le?.message);
            }

            // Keep ALL system_emails docs — each is a unique send event.
            // Only de-dupe email_logs entries that are already represented in system_emails.
            const merged: SystemEmail[] = [...fromSysEmails];
            const sysMessageIds = new Set(fromSysEmails.map(e => e.messageId).filter(Boolean));
            for (const e of fromEmailLogs) {
                if (e.messageId && sysMessageIds.has(e.messageId)) continue;
                merged.push(e);
            }
            merged.sort((a, b) => toMillis(getTimestamp(b)) - toMillis(getTimestamp(a)));

            setEmails(merged);
            setPage(1);
        } catch (error: any) {
            console.error('[SystemEmails] Load error:', error?.message || error);
            showError('Failed to load system emails');
        } finally {
            setIsLoading(false);
        }
    }, [currentUser, showError]);

    /**
     * Resolve the effective type of an email, accounting for legacy records where
     * sender verification emails were stored with type='verification' because the
     * tag wasn't set correctly before the fix. We detect them by subject keyword.
     */
    const resolveType = (e: SystemEmail): string => {
        const raw = String(e.type ?? '').toLowerCase();
        if (raw === 'sender_verification') return 'sender_verification';
        // Legacy: sender verification was stored as type='verification' — identify by subject
        if ((raw === 'verification' || raw === '') &&
            String(e.subject ?? '').toLowerCase().includes('sender')) {
            return 'sender_verification';
        }
        return raw || 'system';
    };

    useEffect(() => {
        const typeFilter = filterType.trim().toLowerCase();
        const statusFilter = filterStatus.trim().toLowerCase();
        const search = searchTerm.trim().toLowerCase();

        const filtered = emails.filter(e => {
            if (typeFilter !== 'all') {
                if (resolveType(e) !== typeFilter) return false;
            }
            if (statusFilter !== 'all') {
                const s = String(e.status ?? '').toLowerCase();
                if (s !== statusFilter) return false;
            }
            if (search) {
                const toText = recipientToText(e.to).toLowerCase();
                const subject = (e.subject ?? '').toLowerCase();
                if (!toText.includes(search) && !subject.includes(search)) return false;
            }
            return true;
        });

        setDisplayed(filtered.slice(0, page * ROWS_PER_PAGE));
    }, [emails, filterType, filterStatus, searchTerm, page]);

    useEffect(() => {
        if (!currentUser) { setIsLoading(false); return; }
        loadEmails();
        loadStats();
    }, [currentUser?.uid]);

    // After emails load, sync stats from actual loaded data (fallback if collection count failed)
    useEffect(() => {
        if (emails.length === 0) return;
        if (stats.total > 0) return; // collection count already worked
        const total = emails.length;
        const failed = emails.filter(e => String(e.status ?? '').toLowerCase() === 'failed').length;
        const success = total - failed;
        setStats({ total, success, failed, successRate: total > 0 ? (success / total) * 100 : 100 });
    }, [emails]);

    // Reset page when filters change
    useEffect(() => { setPage(1); }, [filterType, filterStatus, searchTerm]);

    const handleRefresh = () => {
        setFilterType('all');
        setFilterStatus('all');
        setSearchTerm('');
        loadEmails();
        loadStats();
    };

    // Derive the filtered total for "showing X of Y" display
    const filteredTotal = emails.filter(e => {
        const typeFilter = filterType.trim().toLowerCase();
        const statusFilter = filterStatus.trim().toLowerCase();
        const search = searchTerm.trim().toLowerCase();
        if (typeFilter !== 'all' && resolveType(e) !== typeFilter) return false;
        if (statusFilter !== 'all' && String(e.status ?? '').toLowerCase() !== statusFilter) return false;
        if (search) {
            const hay = `${recipientToText(e.to)} ${e.subject ?? ''}`.toLowerCase();
            if (!hay.includes(search)) return false;
        }
        return true;
    }).length;

    const hasMore = displayed.length < filteredTotal;

    // ─── render ──────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-8">

            {/* Header */}
            <div className="flex justify-between items-start">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">System Emails</h1>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Automated transactional emails sent by the platform — verifications, password resets, billing, sender verification, and more.
                    </p>
                </div>
                <button
                    onClick={handleRefresh}
                    title="Refresh"
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    { label: 'Total Sent', value: stats.total, color: 'border-blue-500' },
                    { label: 'Delivered', value: stats.success, color: 'border-green-500' },
                    { label: 'Failed', value: stats.failed, color: 'border-red-500' },
                    { label: 'Success Rate', value: `${stats.successRate.toFixed(1)}%`, color: 'border-purple-500' },
                ].map(card => (
                    <div key={card.label} className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 ${card.color}`}>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{card.label}</div>
                        <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{card.value}</div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    {/* Search */}
                    <div className="md:col-span-2 relative">
                        <input
                            type="text"
                            placeholder="Search by recipient or subject…"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>

                    {/* Type filter */}
                    <select
                        value={filterType}
                        onChange={e => setFilterType(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-indigo-500 focus:border-indigo-500"
                    >
                        <option value="all">All Types</option>
                        <option value="verification">Email Verification</option>
                        <option value="password_reset">Password Reset</option>
                        <option value="sender_verification">Sender Verification</option>
                        <option value="plan_activation">Plan Activation</option>
                        <option value="invoice">Invoice</option>
                        <option value="receipt">Receipt</option>
                        <option value="billing_notice">Billing Notice</option>
                        <option value="welcome_email">Welcome Email</option>
                        <option value="admin_notification_registration">Registration Alert</option>
                        <option value="admin_message">Admin Message</option>
                        <option value="admin_password_reset">Admin Password Reset</option>
                        <option value="platform_broadcast">Platform Broadcast</option>
                        <option value="system">System (other)</option>
                    </select>

                    {/* Status filter */}
                    <select
                        value={filterStatus}
                        onChange={e => setFilterStatus(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-indigo-500 focus:border-indigo-500"
                    >
                        <option value="all">All Statuses</option>
                        <option value="sent">Sent</option>
                        <option value="failed">Failed</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900/50">
                            <tr>
                                {['Date', 'Recipient', 'Subject', 'Type', 'Provider', 'Status'].map(h => (
                                    <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {isLoading && displayed.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-16 text-center text-gray-500 dark:text-gray-400">
                                        <div className="flex justify-center items-center gap-2">
                                            <svg className="animate-spin h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            Loading system emails…
                                        </div>
                                    </td>
                                </tr>
                            ) : displayed.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-16 text-center">
                                        <svg className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        <p className="mt-3 text-sm font-medium text-gray-700 dark:text-gray-300">No system emails found</p>
                                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">Try adjusting your filters or check back after the platform sends some emails.</p>
                                    </td>
                                </tr>
                            ) : (
                                displayed.map(email => (
                                    <tr key={email.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                                        {/* Date */}
                                        <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                            {formatDate(email)}
                                        </td>

                                        {/* Recipient */}
                                        <td className="px-5 py-3.5 whitespace-nowrap text-sm font-medium">
                                            {email.userId ? (
                                                <Link
                                                    to={`/admin/control-panel-7x9k2m/user-details/${email.userId}`}
                                                    className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 flex items-center gap-1 group"
                                                >
                                                    {recipientToText(email.to) || '—'}
                                                    <svg className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                    </svg>
                                                </Link>
                                            ) : (
                                                <span className="text-gray-900 dark:text-white">{recipientToText(email.to) || '—'}</span>
                                            )}
                                        </td>

                                        {/* Subject */}
                                        <td className="px-5 py-3.5 text-sm text-gray-900 dark:text-white max-w-xs truncate" title={email.subject}>
                                            {email.subject || '—'}
                                        </td>

                                        {/* Type badge */}
                                        <td className="px-5 py-3.5 whitespace-nowrap">
                                            <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeColor(resolveType(email))}`}>
                                                {typeLabel(resolveType(email))}
                                            </span>
                                        </td>

                                        {/* Provider */}
                                        <td className="px-5 py-3.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400 capitalize">
                                            {email.provider || '—'}
                                        </td>

                                        {/* Status badge */}
                                        <td className="px-5 py-3.5 whitespace-nowrap">
                                            <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(email.status)}`}>
                                                {email.status ?? '—'}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer: count + load more */}
                {(displayed.length > 0 || filteredTotal > 0) && (
                    <div className="bg-gray-50 dark:bg-gray-900/50 px-5 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Showing <span className="font-medium text-gray-700 dark:text-gray-300">{displayed.length}</span>
                            {' '}of{' '}
                            <span className="font-medium text-gray-700 dark:text-gray-300">{filteredTotal}</span> emails
                        </p>
                        {hasMore && (
                            <button
                                onClick={() => setPage(p => p + 1)}
                                disabled={isLoading}
                                className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 disabled:opacity-50 transition-colors"
                            >
                                Load more
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminSystemEmails;
